import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, map } from 'rxjs';
import { CookieConsent, COOKIE_CONSENT_SCHEMA_VERSION, DEFAULT_COOKIE_CONSENT } from '../models/cookie-consent.model';

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly storageKey = 'tubify_cookie_consent';
  private readonly isBrowser: boolean;
  private consentSubject = new BehaviorSubject<CookieConsent>(DEFAULT_COOKIE_CONSENT);

  consent$ = this.consentSubject.asObservable();
  adsConsent$ = this.consent$.pipe(map(consent => consent.ads && consent.decided));
  analyticsConsent$ = this.consent$.pipe(map(consent => consent.analytics && consent.decided));
  decided$ = this.consent$.pipe(map(consent => consent.decided));

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.loadConsent();
  }

  acceptAll(): void {
    this.updateConsent({
      ads: true,
      analytics: true,
      decided: true
    });
  }

  rejectNonEssential(): void {
    this.updateConsent({
      ads: false,
      analytics: false,
      decided: true
    });
  }

  setPartialConsent(consent: Partial<Pick<CookieConsent, 'ads' | 'analytics'>>): void {
    const current = this.consentSubject.value;
    this.updateConsent({
      ads: typeof consent.ads === 'boolean' ? consent.ads : current.ads,
      analytics: typeof consent.analytics === 'boolean' ? consent.analytics : current.analytics,
      decided: true
    });
  }

  hasAdsConsent(): boolean {
    const consent = this.consentSubject.value;
    return consent.ads && consent.decided;
  }

  hasDecided(): boolean {
    return this.consentSubject.value.decided;
  }

  resetConsent(): void {
    this.updateConsent({ ...DEFAULT_COOKIE_CONSENT });
  }

  private loadConsent(): void {
    if (!this.isBrowser) {
      return;
    }

    const saved = localStorage.getItem(this.storageKey);
    if (!saved) {
      this.persistConsent(DEFAULT_COOKIE_CONSENT);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      if (this.isValidStoredConsent(parsed)) {
        const normalized: CookieConsent = {
          ...DEFAULT_COOKIE_CONSENT,
          ...parsed,
          essential: true,
          version: COOKIE_CONSENT_SCHEMA_VERSION
        };
        this.consentSubject.next(normalized);
        return;
      }
    } catch {
      // fall through to reset
    }

    this.persistConsent(DEFAULT_COOKIE_CONSENT);
  }

  private isValidStoredConsent(value: unknown): value is CookieConsent {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const candidate = value as Partial<CookieConsent>;
    const shapeValid =
      typeof candidate.essential === 'boolean' &&
      typeof candidate.ads === 'boolean' &&
      typeof candidate.analytics === 'boolean' &&
      typeof candidate.decided === 'boolean' &&
      typeof candidate.version === 'number';

    if (!shapeValid) {
      return false;
    }

    return candidate.version === COOKIE_CONSENT_SCHEMA_VERSION;
  }

  private updateConsent(partial: Partial<CookieConsent>): void {
    const next: CookieConsent = {
      ...DEFAULT_COOKIE_CONSENT,
      ...this.consentSubject.value,
      ...partial,
      essential: true,
      version: COOKIE_CONSENT_SCHEMA_VERSION
    };

    this.consentSubject.next(next);
    this.persistConsent(next);
  }

  private persistConsent(consent: CookieConsent): void {
    if (!this.isBrowser) {
      this.consentSubject.next(consent);
      return;
    }
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(consent));
      this.consentSubject.next(consent);
    } catch {
      // ignore storage errors to avoid breaking UX
    }
  }
}
